import { createApi, fakeBaseQuery } from '@reduxjs/toolkit/query/react'

import { FeatureType } from 'clickNgo/configs/feature'
import { getAccessToken } from 'cng-common/helpers/access'
import { NotificationController } from 'core-components/library/popup'
import { CrudHelper } from 'core/types/helpers/CrudHelper'
import settings from 'src/config/settings'

import { toEspOverview, toEspPartnerData } from '../lib/mapEspResponse'
import { IEspCreateRequestBody, IEspOverviewDto, IEspPartnerDto } from '../model/espDto'
import {
  IEspActivityInput,
  IEspOverview,
  IEspPartnerData,
  IEspSponsorInput,
  IEspSponsorOption,
} from '../model/types'

import { ESP_SPONSOR_LIST } from './espSponsorsCrud'

const ESP_TAG = 'Esp'
const ESP_SPONSORS_TAG = 'EspSponsors'

type TEspApiError = { message: string }

type TJSend<T> =
  | { status: 'success'; data: T }
  | { status: 'fail'; data?: { messages?: { message?: string }[] } }
  | { status: 'error'; message?: string }

/**
 * POSTs to service-partner360 `esp/<action>` and unwraps its JSend answer. A refused rule
 * (`error`) or an invalid body (`fail`) becomes the error of the query, with the service's
 * message.
 */
const postEsp = async <T>(
  action: string,
  body: object,
  signal?: AbortSignal
): Promise<{ data: T } | { error: TEspApiError }> => {
  try {
    const response = await fetch(`${settings.httpsServices.partner360}/api/v0/esp/${action}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAccessToken()}` },
      body: JSON.stringify(body),
      signal,
    })

    if (!response.ok) {
      return { error: { message: `ESP ${action}: ${response.status} ${response.statusText}` } }
    }

    const answer = (await response.json()) as TJSend<T>

    if (answer.status === 'success') {
      return { data: answer.data }
    }

    const message =
      answer.status === 'error'
        ? answer.message
        : answer.data?.messages
            ?.map((item) => item.message)
            .filter(Boolean)
            .join('; ')

    return { error: { message: message || `ESP ${action} failed` } }
  } catch (error) {
    return { error: { message: String(error) } }
  }
}

/** The mail the service sends after a request or a new activity; absent for the actions that send none. */
type TEspMailResult = { status: 'sent' | 'failed' | 'skipped'; reason?: string }

/**
 * Runs a change and surfaces both outcomes as toasts. The change is saved even when its mail did not go out, so that
 * comes as a warning next to the success.
 */
const mutate = async (
  action: string,
  body: object,
  successMessage: string
): Promise<{ data: true } | { error: TEspApiError }> => {
  const result = await postEsp<{ id: number; mail?: TEspMailResult }>(action, body)

  if ('error' in result) {
    NotificationController.showError({ message: result.error.message })

    return result
  }

  NotificationController.showSuccess({ message: successMessage })

  const { mail } = result.data
  if (mail && mail.status !== 'sent') {
    NotificationController.showWarning({
      message: `The email notification was not sent${mail.reason ? `: ${mail.reason}` : ''}`,
    })
  }

  return { data: true }
}

export interface IEspResolveRequestArgs {
  requestId: string
  decision: 'approved' | 'rejected'
  rejectionReason?: string
}

/**
 * The Executive Sponsor Program service (service-partner360, `esp/*`). Every change
 * invalidates the one tag, so the partner's data and the dashboards refetch together.
 */
export const espApi = createApi({
  reducerPath: `${FeatureType.P360Api}/espApi`,
  baseQuery: fakeBaseQuery<TEspApiError>(),
  tagTypes: [ESP_TAG, ESP_SPONSORS_TAG],
  endpoints: (build) => ({
    getEspPartner: build.query<IEspPartnerData, { partnerId: string }>({
      queryFn: async ({ partnerId }, { signal }) => {
        const result = await postEsp<IEspPartnerDto>('partner', { partnerId }, signal)

        return 'error' in result ? result : { data: toEspPartnerData(result.data) }
      },
      providesTags: [ESP_TAG],
    }),

    // The managers the engagement mail copies, named in the Log engagement form before saving.
    getEspManagers: build.query<string[], { partnerId: string }>({
      queryFn: async ({ partnerId }, { signal }) => {
        const result = await postEsp<{ managers: string[] }>('managers', { partnerId }, signal)

        return 'error' in result ? result : { data: result.data.managers }
      },
    }),

    // The sponsor directory (form 2111), every row, for the program team to keep it.
    getEspSponsorList: build.query<IEspSponsorOption[], void>({
      queryFn: async () => {
        try {
          const crud = new CrudHelper(ESP_SPONSOR_LIST.formId, ESP_SPONSOR_LIST.code)
          const response = await crud.read(undefined, { field: 'Name', dir: 'asc' }, 0, 1000)

          return { data: (response?.Data ?? []) as IEspSponsorOption[] }
        } catch (error) {
          return { error: { message: String(error) } }
        }
      },
      providesTags: [ESP_SPONSORS_TAG],
    }),

    saveEspSponsor: build.mutation<true, { entryId?: number; input: IEspSponsorInput }>({
      queryFn: async ({ entryId, input }) => {
        try {
          const crud = new CrudHelper(ESP_SPONSOR_LIST.formId, ESP_SPONSOR_LIST.code)

          if (entryId) {
            await crud.update([{ EntryId: entryId, ...input }], undefined, true)
          } else {
            await crud.create([input], undefined, true)
          }

          NotificationController.showSuccess({ message: 'Sponsor saved' })

          return { data: true }
        } catch (error) {
          NotificationController.showError({ message: 'The sponsor was not saved' })

          return { error: { message: String(error) } }
        }
      },
      invalidatesTags: [ESP_SPONSORS_TAG],
    }),

    getEspOverview: build.query<IEspOverview, void>({
      queryFn: async (_arg, { signal }) => {
        const result = await postEsp<IEspOverviewDto>('overview', {}, signal)

        return 'error' in result ? result : { data: toEspOverview(result.data) }
      },
      providesTags: [ESP_TAG],
    }),

    createEspRequest: build.mutation<true, IEspCreateRequestBody>({
      queryFn: (body) => mutate('request/create', body, 'ESP request submitted for approval'),
      invalidatesTags: [ESP_TAG],
    }),

    deleteEspRequest: build.mutation<true, { requestId: string }>({
      queryFn: ({ requestId }) =>
        mutate('request/delete', { requestId: Number(requestId) }, 'ESP request deleted'),
      invalidatesTags: [ESP_TAG],
    }),

    resolveEspRequest: build.mutation<true, IEspResolveRequestArgs>({
      queryFn: ({ requestId, decision, rejectionReason }) =>
        decision === 'approved'
          ? mutate('request/approve', { requestId: Number(requestId) }, 'ESP request approved')
          : mutate(
              'request/reject',
              { requestId: Number(requestId), reason: rejectionReason ?? '' },
              'ESP request rejected'
            ),
      invalidatesTags: [ESP_TAG],
    }),

    addEspActivity: build.mutation<true, IEspActivityInput>({
      queryFn: (input) => mutate('activity/create', input, 'Engagement saved'),
      invalidatesTags: [ESP_TAG],
    }),

    updateEspActivity: build.mutation<true, { activityId: string; input: IEspActivityInput }>({
      queryFn: ({ activityId, input }) =>
        mutate('activity/update', { ...input, activityId: Number(activityId) }, 'Engagement saved'),
      invalidatesTags: [ESP_TAG],
    }),

    deleteEspActivity: build.mutation<true, { activityId: string }>({
      queryFn: ({ activityId }) =>
        mutate('activity/delete', { activityId: Number(activityId) }, 'Engagement deleted'),
      invalidatesTags: [ESP_TAG],
    }),
  }),
})

export const {
  useGetEspPartnerQuery,
  useGetEspManagersQuery,
  useGetEspOverviewQuery,
  useGetEspSponsorListQuery,
  useSaveEspSponsorMutation,
  useCreateEspRequestMutation,
  useDeleteEspRequestMutation,
  useResolveEspRequestMutation,
  useAddEspActivityMutation,
  useUpdateEspActivityMutation,
  useDeleteEspActivityMutation,
} = espApi
